import React, { useState } from 'react'
import Button from 'components/button'
import Input from 'components/input'
import Popover, { popoverPlacement } from 'components/popover'
import { Calendar } from 'components/icons'
import { IContextMenuState } from 'types'

const WIDTH = 320

/** A day as written in the button: "Oct 12, 2026". Days are yyyy-mm-dd, read as local days, not UTC. */
const shortDay = (day: string) => {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year, month - 1, date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/** What the button says: the range chosen, one end of it, or "Date range". */
export const rangeLabel = (from: string, to: string): string => {
  if (from && to) return `${shortDay(from)} – ${shortDay(to)}`
  if (from) return `From ${shortDay(from)}`
  if (to) return `Until ${shortDay(to)}`
  return 'Date range'
}

interface DateRangeProps {
  /** The first day, yyyy-mm-dd, or '' for none. */
  from: string
  /** The last day, yyyy-mm-dd, or '' for none. */
  to: string
  onChange: (range: { from: string; to: string }) => void
}

/**
 * A date range filter, in the manner of shadcn/ui's date range picker: one button saying the range chosen
 * ("Oct 1, 2026 – Oct 12, 2026", or "Date range" for any), opening a popover with the first and last day and a
 * way to clear them. Either end can be left open.
 */
const DateRange: React.FC<DateRangeProps> = ({ from, to, onChange }) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const chosen = from !== '' || to !== ''

  const day = (id: string, label: string, value: string, change: (value: string) => void, range: object) => (
    <label className="flex flex-col gap-1.5" htmlFor={id}>
      <span className="text-[13px] font-medium text-body">{label}</span>
      <Input
        type="date"
        id={id}
        name={id}
        value={value}
        onChange={(event) => change(event.target.value)}
        className="h-8 cursor-pointer"
        {...range}
      />
    </label>
  )

  return (
    <>
      <Button
        internalType="outline"
        aria-haspopup="dialog"
        aria-expanded={!!at}
        // Its value in the text's colour, grey while no range is chosen; the icon at the end, at the text's size.
        className={`h-8 font-normal ${chosen ? 'text-body' : 'text-muted'}`}
        // The popover closes on a press outside it, and this button counts as outside: while it's open, keep
        // the press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (at) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAt(
            at
              ? null
              : popoverPlacement(event.currentTarget.getBoundingClientRect(), {
                  align: 'start',
                  width: WIDTH,
                  height: 220
                })
          )
        }
      >
        {rangeLabel(from, to)}
        <Calendar className="size-3.5" />
      </Button>
      <Popover
        id="date-range"
        at={at}
        onClose={() => setAt(null)}
        label="Date range"
        title="Date range"
        width={WIDTH}
        footer={
          chosen && (
            <Button internalType="outline" onClick={() => onChange({ from: '', to: '' })}>
              Clear
            </Button>
          )
        }
      >
        <div className="grid grid-cols-2 gap-2">
          {day('start-date', 'From', from, (value) => onChange({ from: value, to }), { max: to || undefined })}
          {day('end-date', 'To', to, (value) => onChange({ from, to: value }), { min: from || undefined })}
        </div>
      </Popover>
    </>
  )
}

export default DateRange
